import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Droplets } from "lucide-react";

const pct = (v, t) => (t ? Math.min(100, Math.round((v / t) * 100)) : 0);
const fmt = (iso) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

export function TrackingHistory({ memberId }) {
  const { t } = useLang();
  const { data } = useQuery({ queryKey: ["tracking-history"], queryFn: async () => (await api.get("/tracking/history", { params: { days: 28 } })).data });
  const member = data?.members?.find((m) => m.member_id === memberId);
  if (!data || !member) return null;
  const target = member.targets;
  const dates = data.dates;
  const last7 = dates.slice(-7);
  const weeks = [0, 1, 2, 3].map((i) => dates.slice(i * 7, i * 7 + 7));

  const tot = (d) => d.by_member?.[memberId];
  const water = (d) => d.water?.[memberId] || 0;
  const waterGoal = member.water_goal || 8;

  return (
    <div className="mt-6 border-t border-border pt-5" data-testid="tracking-history">
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{t("history_title")} · {t("last_7_days")}</p>
      <div className="mt-3 grid grid-cols-7 gap-1.5 items-end h-28" data-testid="history-7days">
        {last7.map((d) => {
          const v = tot(d)?.calories || 0;
          const p = pct(v, target.calories);
          const over = v > target.calories * 1.1;
          return (
            <div key={d.date} className="flex flex-col items-center justify-end h-full gap-1" title={`${d.date}: ${v} / ${target.calories} kcal`} data-testid={`history-day-${d.date}`}>
              <span className="text-[10px] text-muted-foreground">{v ? Math.round(p) + "%" : ""}</span>
              <div className="w-full rounded-t-lg bg-secondary relative overflow-hidden" style={{ height: "70%" }}>
                <div className={`absolute bottom-0 w-full rounded-t-lg transition-[height] duration-500 ${over ? "bg-chenia-terracottadark" : "bg-primary"}`} style={{ height: `${p}%` }} />
              </div>
              <span className={`text-[10px] inline-flex items-center gap-0.5 ${water(d) >= waterGoal ? "text-primary font-semibold" : "text-muted-foreground"}`} data-testid={`history-water-${d.date}`}><Droplets className="h-2.5 w-2.5" />{water(d)}</span>
              <span className="text-[10px] text-muted-foreground">{t(["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][new Date(d.date).getUTCDay()]).slice(0, 2)}</span>
            </div>
          );
        })}
      </div>

      <p className="mt-5 text-xs uppercase tracking-widest text-muted-foreground font-semibold">{t("last_4_weeks")}</p>
      <div className="mt-2 space-y-1.5" data-testid="history-weeks">
        {weeks.map((w, i) => {
          const tracked = w.filter((d) => tot(d)?.calories);
          const n = tracked.length;
          const avg = (k) => (n ? Math.round(tracked.reduce((s, d) => s + (tot(d)?.[k] || 0), 0) / n) : 0);
          const wDays = w.filter((d) => water(d) > 0);
          const wAvg = wDays.length ? Math.round(wDays.reduce((s, d) => s + water(d), 0) / wDays.length) : 0;
          return (
            <div key={w[0].date} className="flex items-center gap-3 rounded-2xl bg-secondary/60 px-3 py-2" data-testid={`history-week-${i}`}>
              <span className="text-[11px] font-semibold w-24 shrink-0">{fmt(w[0].date)} – {fmt(w[6].date)}</span>
              <div className="flex-1 h-2 rounded-full bg-background overflow-hidden">
                <div className="h-full rounded-full bg-primary" style={{ width: `${pct(avg("calories"), target.calories)}%` }} />
              </div>
              <span className="text-xs text-right w-36 shrink-0">
                {n ? <>{avg("calories")} <span className="text-muted-foreground">/ {target.calories} · P {pct(avg("protein"), target.protein)}%</span></> : <span className="text-muted-foreground">{t("no_data")}</span>}
              </span>
              <span className="text-[10px] text-muted-foreground w-10 text-right shrink-0 inline-flex items-center justify-end gap-0.5" data-testid={`history-week-water-${i}`}><Droplets className="h-2.5 w-2.5" />{wAvg}</span>
              <span className="text-[10px] text-muted-foreground w-12 text-right shrink-0">{n}/7 j</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
